/**
 * OPERATION BLACKOUT — HUD system.  Owner: ui.
 *
 * Everything the player reads is assembled here and driven off the event bus.
 * Nothing polls another system for state that an event already carries.
 *
 * Cost model: five small 2D canvases + one full-screen marker canvas, each
 * redrawn only when its own state changed. No layout-triggering CSS property is
 * ever animated — DOM motion is transform/opacity only, written from JS so it
 * survives Playwright's `animations: 'disabled'`.
 */
import * as THREE from 'three';
import { registerShot, look } from '../core/Shots.js';
import { COL, clamp01, lerp, smoothstep, approach, devicePx } from './Theme.js';
import { Reticle, HM_BODY, HM_HEAD, HM_KILL } from './Reticle.js';
import { AmmoBlock } from './Ammo.js';
import { Compass, headingFromYaw, bearingTo } from './Compass.js';
import { Minimap } from './Minimap.js';
import { Markers } from './Markers.js';
import { Killfeed } from './Killfeed.js';
import { ScoreBoard, GearBlock } from './Panels.js';
import { Menus } from './Menus.js';
import { IntroOverlay } from './IntroOverlay.js';

const STANCE = ['STANDING', 'CROUCHED', 'PRONE'];

export class HUD {
  constructor() {
    this.ctx = null;
    this.clock = 0;
    this.freeze = false;
    this.ms = 0;
    this.enabled = {
      compass: true, minimap: true, killfeed: true, worldMarkers: true,
    };
    this.hudOpacity = 1;
    this.hudScale = 1;

    this.s = {
      /* weapon */
      mag: 30, reserve: 120, magMax: 30,
      weaponName: 'M4A1 CARBINE', fireMode: 'AUTO', attachment: 'CQB · 1× HOLO',
      weaponClass: 'rifle',
      reloading: false, reloadT: 0, reloadDur: 2.1,
      /* aim */
      spread: 0.020, ads: 0, pxPerRad: 700,
      /* player */
      health: 100, maxHealth: 100, stance: 0, stanceLabel: 'STANDING',
      moving: 0, speed: 0,
      lethal: 1, tactical: 2, fieldCharge: 0.62,
      /* world */
      yaw: 0, heading: 0, camX: 0, camZ: 0,
      /* meta */
      scoreA: 48, scoreB: 41, scoreLimit: 75, matchTime: 462,
      lowHealth: 0, beat: 0, time: 0,
      blips: [], mapMarkers: [],
    };

    // Fallback ballistics model used until ctx.weapons publishes `spread`.
    this.fb = { bloom: 0, move: 0 };
    this.markers = [];
    this.blips = [];
    this._vig = -1;
    this._sat = -1;
    this._lastMenu = null;
  }

  /* ------------------------------------------------------------------ init */

  init(ctx) {
    this.ctx = ctx;
    const root = document.getElementById('ui-root');
    if (!root) return;
    this.root = root;

    this.wrap = document.createElement('div');
    this.wrap.id = 'hud';
    this.wrap.className = 'hud';
    root.appendChild(this.wrap);

    // Low-health treatments go in FIRST so their backdrop-filter only ever
    // samples the 3D frame — the HUD canvases paint on top of them and keep
    // their own colour.
    this.lowhp = div('hud-lowhp');
    this.wrap.appendChild(this.lowhp);
    this.vig = div('hud-vignette');
    this.wrap.appendChild(this.vig);

    this.markersL = new Markers(this.wrap);
    this.minimap = new Minimap(this.wrap);
    this.score = new ScoreBoard(this.wrap);
    this.compass = new Compass(this.wrap);
    this.killfeed = new Killfeed(this.wrap);
    this.gear = new GearBlock(this.wrap);
    this.ammo = new AmmoBlock(this.wrap);
    this.reticle = new Reticle(this.wrap);

    this.menuLayer = div('hud-menu-layer');
    root.appendChild(this.menuLayer);
    this.menus = new Menus(this);
    this.menus.init(ctx, this.menuLayer);

    this.intro = new IntroOverlay(this);
    this.intro.init(ctx, root);

    this._bind(ctx);
    this._resize(ctx.engine?.width || window.innerWidth, ctx.engine?.height || window.innerHeight);

    try { this.minimap.build(ctx); } catch (e) { console.warn('[ui] minimap build failed', e); }

    this._seedDemoMarkers(ctx);
    this._registerShots();

    ctx.hud = {
      hud: this,
      open: (m) => this.menus.open(m),
      close: () => this.menus.close(),
      setMarkers: (list) => { this.markers = list.slice(); },
      addMarker: (m) => { this.markers.push(m); return m; },
      removeMarker: (m) => { const i = this.markers.indexOf(m); if (i >= 0) this.markers.splice(i, 1); },
      rebuildMinimap: () => this.minimap.build(ctx),
      killfeed: (e) => this.killfeed.push(e),
      setScore: (a, b, limit) => { this.s.scoreA = a; this.s.scoreB = b; if (limit) this.s.scoreLimit = limit; },
      get ms() { return this.hud.ms; },
    };

    // Cooperate with pointer lock: losing it mid-game means the player hit
    // Escape or alt-tabbed, which is exactly when a pause menu should appear.
    this._onLock = () => {
      const locked = !!document.pointerLockElement;
      if (locked) this._wasLocked = true;
      else if (this._wasLocked && !this.menus.isOpen && !this.intro?.visible) {
        this._wasLocked = false;
        this.menus.open('pause');
      }
    };
    document.addEventListener('pointerlockchange', this._onLock);

    if (new URLSearchParams(location.search).has('menu')) this.menus.open('main');
  }

  dispose() {
    document.removeEventListener('pointerlockchange', this._onLock);
    for (const off of this._off || []) off();
    this.menus?.dispose();
    this.intro?.dispose();
    this.wrap?.remove();
    this.menuLayer?.remove();
  }

  /* ---------------------------------------------------------------- events */

  _bind(ctx) {
    const ev = ctx.events;
    if (!ev) return;
    const on = (t, f) => offs.push(ev.on(t, f));
    const offs = [];
    const s = this.s;

    on('weapon:ammo', (p) => {
      if (!p) return;
      const prev = s.mag;
      if (p.max != null) s.magMax = p.max;
      if (p.mag != null) s.mag = p.mag;
      if (p.reserve != null) s.reserve = p.reserve;
      if (s.mag !== prev) this.ammo.punch(Math.sign(s.mag - prev));
      this.ammo.dirty = true;
    });

    on('weapon:reload', (p) => {
      if (!p) return;
      if (p.phase === 'start') {
        s.reloading = true;
        s.reloadT = 0;
        s.reloadDur = p.duration ?? p.time ?? 2.1;
      } else {
        s.reloading = false;
        s.reloadT = 0;
      }
      this.ammo.dirty = true;
    });

    on('weapon:switch', (p) => {
      const w = p?.to;
      if (w) {
        s.weaponName = String(w.displayName ?? w.name ?? w).toUpperCase();
        s.weaponClass = w.class ?? w.category ?? s.weaponClass;
        if (w.fireMode) s.fireMode = String(w.fireMode).toUpperCase();
        if (w.magSize) s.magMax = w.magSize;
      }
      this.ammo.swap();
    });

    on('weapon:fire', (p) => {
      this.reticle.fired();
      this.fb.bloom = Math.min(1, this.fb.bloom + 0.26);
      // Enemy muzzle flashes light up the minimap the way an unsuppressed shot
      // does in COD; our own shots do not.
      const o = p?.origin;
      const cam = this.ctx?.camera;
      if (o && cam && o.distanceTo(cam.position) > 3) {
        this.blips.push({ x: o.x, z: o.z, t: 0, life: 2.2 });
        if (this.blips.length > 24) this.blips.shift();
      }
    });

    on('damage:dealt', (p) => {
      if (!p) return;
      this.reticle.hit(p.killed ? HM_KILL : p.headshot ? HM_HEAD : HM_BODY);
      if (p.killed) {
        this.killfeed.push({
          attacker: 'OPERATOR', victim: nameOf(p.entity), weapon: s.weaponClass,
          headshot: !!p.headshot, mine: true, victimFriendly: false,
        });
        s.scoreA = Math.min(s.scoreLimit, s.scoreA + 1);
      }
    });

    on('damage:taken', (p) => {
      if (!p) return;
      if (p.health != null) s.health = p.health;
      else s.health = Math.max(0, s.health - (p.amount || 0));
      const d = p.fromDir;
      if (d) this.reticle.damage(Math.atan2(d.x, -d.z));
      this._hitFlash = 1;
    });

    on('player:state', (p) => {
      if (!p) return;
      s.speed = p.speed ?? s.speed;
      s.stance = p.crouched ? 1 : p.state === 'prone' ? 2 : 0;
      s.stanceLabel = p.state === 'slide' ? 'SLIDING'
        : p.state === 'sprint' ? 'SPRINTING'
          : p.state === 'air' || p.state === 'mantle' ? 'AIRBORNE'
            : STANCE[s.stance];
      s.moving = clamp01((p.speed ?? 0) / 5.5);
    });

    on('ai:spawn', (p) => { if (p?.entity) this._maybeAlly(p.entity); });
    on('ai:death', (p) => {
      const e = p?.entity;
      if (!e) return;
      const m = this.markers.find((x) => x.entity === e);
      if (m) this.markers.splice(this.markers.indexOf(m), 1);
    });

    on('fx:explosion', (p) => {
      if (!p?.position) return;
      this.blips.push({ x: p.position.x, z: p.position.z, t: 0, life: 3.0 });
    });

    this._off = offs;
  }

  _maybeAlly(e) {
    if (!e || !e.position || e.team === 'enemy' || e.hostile) return;
    this.markers.push({ entity: e, position: e.position, kind: 'ally', label: (e.name || 'ALLY').toUpperCase() });
  }

  /* ----------------------------------------------------------- HUD options */

  applyHudSetting(id, v) {
    const r = this.reticle;
    switch (id) {
      case 'crosshairDot': r.opt.dot = !!v; break;
      case 'crosshairOpacity': r.opt.opacity = v; break;
      case 'crosshairThickness': r.opt.thickness = Math.round(v); break;
      case 'hitMarkers': r.opt.hitMarkers = !!v; break;
      case 'damageIndicators': r.opt.damageIndicators = !!v; break;
      case 'hudOpacity': this.hudOpacity = v; this.wrap.style.opacity = String(v); break;
      case 'hudScale':
        this.hudScale = v;
        this.wrap.style.setProperty('--hud-scale', String(v));
        break;
      case 'compass': this.enabled.compass = !!v; this.compass.cv.style.display = v ? '' : 'none'; break;
      case 'minimap':
        this.enabled.minimap = !!v;
        this.minimap.cv.style.display = v ? '' : 'none';
        this.score.cv.style.display = v ? '' : 'none';
        break;
      case 'killfeed': this.enabled.killfeed = !!v; this.killfeed.cv.style.display = v ? '' : 'none'; break;
      case 'worldMarkers': this.enabled.worldMarkers = !!v; this.markersL.cv.style.display = v ? '' : 'none'; break;
      default: break;
    }
    r.dirty = true;
  }

  onMenuChange(mode) {
    // The gameplay overlay never fights a menu for attention.
    this.wrap.classList.toggle('behind-menu', mode === 'pause');
    this.wrap.style.display = (mode === 'main' || mode === 'settings') ? 'none' : '';
    const input = this.ctx?.input;
    if (input && !mode) input.enabled = true;
    this._lastMenu = mode;
  }

  /* -------------------------------------------------------------- resizing */

  resize(w, h) { this._resize(w, h); }

  _resize(w, h) {
    this.W = w; this.H = h;
    const dpr = devicePx();
    this.markersL.resize(w, h, dpr);
    this.minimap.resize(dpr);
    this.score.resize(dpr);
    this.compass.resize(dpr);
    this.killfeed.resize(dpr);
    this.gear.resize(dpr);
    this.ammo.resize(dpr);
    this.reticle.resize(dpr);
    if (this.wrap) this.wrap.style.setProperty('--safe', `${Math.round(Math.min(w, h) * 0.028)}px`);
  }

  /* ---------------------------------------------------------------- update */

  update(dt, ctx) {
    if (!this.wrap) return;
    const t0 = performance.now();
    const s = this.s;
    if (!this.freeze) this.clock += dt;
    s.time = this.clock;
    const adt = this.freeze ? 0 : dt;

    const cam = ctx.camera;
    if (cam) {
      s.camX = cam.position.x;
      s.camZ = cam.position.z;
      s.yaw = cam.rotation.y;
      s.heading = -cam.rotation.y;
      s.pxPerRad = (this.H * 0.5) / Math.tan(THREE.MathUtils.degToRad(cam.fov) * 0.5);
    }

    /* ------------------------------------------------------ weapon state */
    const W = ctx.weapons;
    s.ads = clamp01(W?.ads ?? 0);
    if (this.spreadOverride != null) {
      s.spread = this.spreadOverride;
    } else if (W && typeof W.spread === 'number' && W.spread > 0) {
      s.spread = W.spread;
    } else {
      // Fallback cone: stance + movement + sustained fire, so the reticle is
      // alive and judgeable before the weapons module lands.
      this.fb.bloom = Math.max(0, this.fb.bloom - adt * 0.9);
      this.fb.move = approach(this.fb.move, s.moving, 0.18, adt);
      const stanceMul = s.stance === 1 ? 0.62 : 1;
      const adsMul = lerp(1, 0.22, s.ads);
      s.spread = (0.017 + this.fb.move * 0.030 + this.fb.bloom * 0.026) * stanceMul * adsMul;
    }
    if (typeof W?.magMax === 'number') s.magMax = W.magMax;

    if (s.reloading) {
      s.reloadT += adt;
      if (s.reloadT >= s.reloadDur) {
        s.reloadT = s.reloadDur;
        if (!this.freeze) {
          // Weapons owns the real ammo transaction; if it never emits an `end`
          // we still close out the arc so the HUD cannot get stuck.
          s.reloading = false;
          const need = Math.max(0, s.magMax - s.mag);
          const take = Math.min(need, s.reserve);
          if (take > 0) { s.mag += take; s.reserve -= take; this.ammo.punch(1); }
        }
      }
      this.ammo.dirty = true;
    }

    /* ------------------------------------------------------ player state */
    const P = ctx.player;
    if (P) {
      if (typeof P.health === 'number') s.health = P.health;
      if (this.healthOverride != null) s.health = this.healthOverride;
      if (typeof P.maxHealth === 'number') s.maxHealth = P.maxHealth;
      if (P.state) {
        s.stance = P.crouched ? 1 : P.state === 'prone' ? 2 : 0;
        s.stanceLabel = P.state === 'slide' ? 'SLIDING'
          : P.sprinting || P.state === 'sprint' ? 'SPRINTING'
            : P.state === 'air' ? 'AIRBORNE' : STANCE[s.stance];
      }
      if (typeof P.speed === 'number') s.moving = clamp01(P.speed / 5.5);
    }

    const hp = clamp01(s.health / Math.max(1, s.maxHealth));
    s.lowHealth = smoothstep(0.52, 0.16, hp);
    // Heart rate climbs as health falls: ~70 bpm healthy, ~132 bpm critical.
    const bpm = lerp(72, 132, s.lowHealth) / 60;
    s.beat = s.lowHealth > 0.001 ? thump((this.clock * bpm) % 1) : 0;
    this._applyLowHealth(ctx, s);

    /* ------------------------------------------------------------- timers */
    if (!this.freeze) {
      s.matchTime = Math.max(0, s.matchTime - dt);
      for (let i = this.blips.length - 1; i >= 0; i--) {
        this.blips[i].t += dt;
        if (this.blips[i].t > this.blips[i].life) this.blips.splice(i, 1);
      }
    }
    s.blips = this.blips;

    this.reticle.advance(adt);
    this.ammo.advance(adt);
    this.killfeed.advance(adt);

    /* -------------------------------------------------------------- draw */
    if (this.menus.isOpen) {
      this.menus.tick();
      if (this._lastMenu === 'main' || this._lastMenu === 'settings') {
        this.ms = performance.now() - t0;
        return;
      }
    }

    if (this.reticle.dirty || this._spreadMoved(s)) this.reticle.draw(s);
    if (this.ammo.dirty) this.ammo.draw(s);
    if (this.killfeed.dirty && this.enabled.killfeed) this.killfeed.draw();

    const heading = headingFromYaw(s.yaw);
    if (this.enabled.compass && this.compass.needsRedraw(heading)) {
      this.compass.draw(heading, this._compassMarkers(s));
    }

    if (this.enabled.minimap) {
      s.mapMarkers = this._mapMarkers();
      this.minimap.draw(s);
      this.score.draw(s);
    }
    this.gear.draw(s);

    if (this.enabled.worldMarkers) this.markersL.draw(this.markers, cam, s.ads);

    this.ms = this.ms * 0.9 + (performance.now() - t0) * 0.1;
  }

  _spreadMoved(s) {
    const px = Math.tan(s.spread) * s.pxPerRad;
    if (this._lastSpreadPx === undefined || Math.abs(px - this._lastSpreadPx) > 0.12) {
      this._lastSpreadPx = px;
      return true;
    }
    if (this._lastAds !== s.ads) { this._lastAds = s.ads; return true; }
    if (this._lastBeat !== s.beat) { this._lastBeat = s.beat; return true; }
    return false;
  }

  _applyLowHealth(ctx, s) {
    const lh = s.lowHealth;
    const power = clamp01(lh * (0.55 + 0.45 * s.beat) + (this._hitFlash || 0) * 0.5);
    if (this._hitFlash) this._hitFlash = Math.max(0, this._hitFlash - 0.08);

    const R = ctx.render || ctx.get?.('render');
    const hasApi = typeof R?.damageVignette === 'function';
    if (hasApi) {
      if (Math.abs(power - this._vig) > 0.01) { R.damageVignette(power); this._vig = power; }
      this.vig.style.opacity = '0';
    } else {
      // Bridge until the render team's damageVignette lands. Self-disables the
      // frame that API appears — see the report note.
      this.vig.style.opacity = (power * 0.85).toFixed(3);
    }

    // Desaturation cue is HUD-side on purpose: it is a backdrop filter over the
    // composited frame, not a duplicate of the render vignette.
    const sat = 1 - lh * 0.46;
    if (Math.abs(sat - this._sat) > 0.02) {
      this._sat = sat;
      this.lowhp.style.backdropFilter = lh > 0.01 ? `saturate(${sat.toFixed(2)}) contrast(${(1 + lh * 0.07).toFixed(2)})` : '';
      this.lowhp.style.webkitBackdropFilter = this.lowhp.style.backdropFilter;
    }
  }

  _compassMarkers(s) {
    const out = [];
    for (const m of this.markers) {
      if (m.kind !== 'objective') continue;
      out.push({
        bearing: bearingTo(m.position.x - s.camX, m.position.z - s.camZ),
        label: m.label || '?',
        color: m.color || COL.amber,
      });
    }
    return out;
  }

  _mapMarkers() {
    const out = [];
    for (const m of this.markers) {
      out.push({
        x: m.position.x, z: m.position.z, kind: m.kind,
        label: m.label, yaw: m.yaw ?? m.entity?.yaw ?? 0,
      });
    }
    return out;
  }

  /* ----------------------------------------------------------------- shots */

  resetState(ctx) {
    const s = this.s;
    this.clock = 0;
    this.freeze = true;
    this.blips.length = 0;
    this.reticle.hits.length = 0;
    this.reticle.dmg.length = 0;
    this.reticle.firePunch = 0;
    this.reticle.dirty = true;
    this.killfeed.rows.length = 0;
    this.killfeed.dirty = true;
    this.ammo.magPunch = 0;
    this.ammo.swapT = 1;
    this.ammo.dirty = true;
    this.fb.bloom = 0; this.fb.move = 0;
    this._hitFlash = 0;
    this.spreadOverride = null;
    this.healthOverride = null;
    this._lastSpreadPx = undefined;
    this._vig = -1; this._sat = -1;
    this.compass.dirty = true;
    this.menus.close();
    if (this.root) this.root.style.display = '';
    this.wrap.style.display = '';
    this.wrap.style.opacity = '';   // let .behind-menu win when a menu opens

    Object.assign(s, {
      mag: 24, reserve: 120, magMax: 30,
      weaponName: 'M4A1 CARBINE', fireMode: 'AUTO', attachment: 'CQB · 1× HOLO',
      weaponClass: 'rifle',
      reloading: false, reloadT: 0, reloadDur: 2.1,
      spread: 0.021, ads: 0,
      health: 100, maxHealth: 100, stance: 0, stanceLabel: 'STANDING',
      moving: 0, speed: 0, lethal: 1, tactical: 2, fieldCharge: 0.62,
      scoreA: 48, scoreB: 41, scoreLimit: 75, matchTime: 462,
      lowHealth: 0, beat: 0,
    });
  }

  /** Deterministic gameplay framing shared by every HUD shot. */
  _framing(ctx) {
    const sp = ctx.level?.spawnPoints?.[0];
    const from = sp ? [sp.x, sp.y, sp.z] : [3.2, 1.72, 11.5];
    // Aim at the level centre at roughly eye height so the shot works whatever
    // the level team has built this hour.
    const b = ctx.level?.navBounds || ctx.level?.bounds;
    const at = b && b.isBox3 && !b.isEmpty()
      ? [(b.min.x + b.max.x) / 2, from[1] - 0.22, (b.min.z + b.max.z) / 2]
      : [-2.4, 1.15, -3.5];
    return { from, at };
  }

  _stage(ctx) {
    this.resetState(ctx);
    const { from, at } = this._framing(ctx);
    look(ctx, from, at, 78);
    this._seedDemoMarkers(ctx);
    this.update(0, ctx);
  }

  /**
   * Placeholder objectives/teammates positioned relative to whatever level is
   * loaded, so the HUD can be judged before level and AI land. Replaced wholesale
   * by `ctx.hud.setMarkers()` as soon as those systems publish real entities.
   */
  _seedDemoMarkers(ctx) {
    const b = ctx.level?.navBounds || ctx.level?.bounds;
    const sp = ctx.level?.spawnPoints?.[0];
    const ox = b && b.isBox3 && !b.isEmpty() ? (b.min.x + b.max.x) / 2 : 0;
    const oz = b && b.isBox3 && !b.isEmpty() ? (b.min.z + b.max.z) / 2 : 0;
    const ex = b && b.isBox3 && !b.isEmpty() ? Math.min(28, (b.max.x - b.min.x) * 0.28) : 14;
    const ez = b && b.isBox3 && !b.isEmpty() ? Math.min(28, (b.max.z - b.min.z) * 0.28) : 14;
    const py = (sp?.y ?? 1.65) - 0.1;
    const P = (dx, dz) => new THREE.Vector3(ox + dx, py, oz + dz);
    this.markers = [
      { position: P(ex, -ez), kind: 'objective', label: 'A' },
      { position: P(-ex, ez * 0.3), kind: 'objective', label: 'B' },
      { position: P(-ex * 0.35, ez * 0.1), kind: 'ally', label: 'HAWKES', yaw: 0.6 },
      { position: P(ex * 0.42, ez * 0.18), kind: 'ally', label: 'VARGAS', yaw: -1.9 },
      { position: P(ex * 0.08, -ez * 1.3), kind: 'ally', label: 'RUIZ', yaw: 2.7 },
    ];
  }

  _registerShots() {
    const hud = this;

    registerShot('ui_hud', (ctx) => {
      hud._stage(ctx);
      const s = hud.s;
      s.mag = 24; s.reserve = 120; s.moving = 0.55;
      hud.fb.move = 0.55; hud.fb.bloom = 0.18;
      hud.spreadOverride = 0.0295;
      hud.killfeed.push({ attacker: 'HAWKES', victim: 'KOSLOV', weapon: 'smg', victimFriendly: false });
      hud.killfeed.push({ attacker: 'OPERATOR', victim: 'BRAGIN', weapon: 'rifle', headshot: true, mine: true, victimFriendly: false });
      hud.killfeed.push({ attacker: 'VOLKOV', victim: 'RUIZ', weapon: 'sniper', attackerFriendly: false, victimFriendly: true });
      hud.killfeed.rows[0].t = 3.6;
      hud.killfeed.rows[1].t = 1.9;
      hud.killfeed.rows[2].t = 0.34;
      hud.blips.push({ x: 12.0, z: -12.0, t: 0.5, life: 2.2 });
      hud.blips.push({ x: -9.0, z: -6.5, t: 1.4, life: 2.2 });
      hud._redrawAll(ctx);
    }, { settle: 2, desc: 'full in-game HUD over gameplay' });

    registerShot('ui_hitmarker', (ctx) => {
      hud._stage(ctx);
      const s = hud.s;
      s.mag = 19; hud.spreadOverride = 0.041; hud.fb.bloom = 0.7;
      hud.reticle.firePunch = 0.55;
      hud.reticle.hits.push({ t: 0.036, kind: HM_BODY });   // peak of the 120 ms curve
      hud.killfeed.push({ attacker: 'HAWKES', victim: 'KOSLOV', weapon: 'smg', victimFriendly: false });
      hud.killfeed.rows[0].t = 2.4;
      hud._redrawAll(ctx);
    }, { settle: 2, desc: 'hit marker frozen at peak' });

    registerShot('ui_hitmarker_kill', (ctx) => {
      hud._stage(ctx);
      const s = hud.s;
      s.mag = 11; hud.spreadOverride = 0.044; hud.fb.bloom = 0.8;
      hud.reticle.firePunch = 0.7;
      hud.reticle.hits.push({ t: 0.052, kind: HM_KILL });
      hud.killfeed.push({ attacker: 'OPERATOR', victim: 'VOLKOV', weapon: 'rifle', headshot: true, mine: true, victimFriendly: false });
      hud.killfeed.rows[0].t = 0.14;
      hud.s.scoreA = 49;
      hud._redrawAll(ctx);
    }, { settle: 2, desc: 'kill marker + killfeed entry sliding in' });


    registerShot('ui_reload', (ctx) => {
      hud._stage(ctx);
      const s = hud.s;
      s.mag = 0; s.reserve = 120; s.reloading = true; s.reloadDur = 2.1; s.reloadT = 1.16;
      hud.spreadOverride = 0.024;
      hud.killfeed.push({ attacker: 'VARGAS', victim: 'ORLOV', weapon: 'rifle', victimFriendly: false });
      hud.killfeed.rows[0].t = 2.9;
      hud._redrawAll(ctx);
    }, { settle: 2, desc: 'mid-reload with the progress arc' });

    registerShot('ui_menu', (ctx) => {
      hud.resetState(ctx);
      const f = hud._framing(ctx);
      look(ctx, f.from, f.at, 52);
      hud.menus.open('main');
    }, { settle: 2, desc: 'main menu' });

    registerShot('ui_pause', (ctx) => {
      hud._stage(ctx);
      hud.s.mag = 17;
      hud._redrawAll(ctx);
      hud.menus.open('pause');
    }, { settle: 2, desc: 'pause menu over the dimmed HUD' });

    registerShot('ui_settings', (ctx) => {
      hud.resetState(ctx);
      const f = hud._framing(ctx);
      look(ctx, f.from, f.at, 52);
      hud.menus.open('main');
      hud.menus.open('settings');
      hud.menus._setTab('video');
      const row = hud.menuLayer.querySelector('.set-page.show .set-row');
      if (row) row.dispatchEvent(new Event('mouseenter'));
    }, { settle: 2, desc: 'settings panel' });

    // Registered LAST on purpose: ctx.render.damageVignette() is a monotonic
    // request with a ~1.1/s decay and no way to clear it, so any shot that ran
    // after this one would inherit the red edge. See the report.
    registerShot('ui_damage', (ctx) => {
      hud._stage(ctx);
      const s = hud.s;
      // The player system owns health; the shot overrides the HUD's view of it
      // rather than writing into another team's state.
      s.mag = 8; s.reserve = 60; hud.spreadOverride = 0.036;
      hud.healthOverride = 17;
      s.health = 17;
      hud.fb.bloom = 0.4;
      // Freeze the clock on a heartbeat systole: at 17 hp the rate is ~132 bpm,
      // so 0.48 s lands 0.055 into a beat — the peak of the thump envelope.
      hud.clock = 0.48;
      s.time = hud.clock;
      const h = -ctx.camera.rotation.y;
      hud.reticle.dmg.push({ angle: h + 2.25, t: 0.10 });
      hud.reticle.dmg.push({ angle: h - 1.15, t: 0.52 });
      hud.reticle.dmg.push({ angle: h + 0.42, t: 0.92 });
      hud.blips.push({ x: 9.5, z: 7.5, t: 0.2, life: 2.2 });
      hud.update(0, ctx);
      hud._redrawAll(ctx);
    }, { settle: 24, desc: 'damage direction indicators + low-health state' });
  }

  _redrawAll(ctx) {
    const s = this.s;
    s.blips = this.blips;
    s.mapMarkers = this._mapMarkers();
    this.reticle.draw(s);
    this.ammo.draw(s);
    this.killfeed.draw();
    this.compass.dirty = true;
    this.compass.draw(headingFromYaw(s.yaw), this._compassMarkers(s));
    this.minimap.draw(s);
    this.score.draw(s);
    this.gear.draw(s);
    this.markersL.draw(this.markers, ctx.camera, s.ads);
  }
}

/* ---------------------------------------------------------------- helpers */

function div(cls) {
  const n = document.createElement('div');
  n.className = cls;
  return n;
}

function nameOf(e) {
  if (!e) return 'HOSTILE';
  return String(e.name || e.callsign || e.id || 'HOSTILE').toUpperCase();
}

/** Double-thump heartbeat envelope, p in [0,1). */
function thump(p) {
  const a = Math.exp(-Math.pow((p - 0.05) / 0.052, 2));
  const b = 0.6 * Math.exp(-Math.pow((p - 0.23) / 0.058, 2));
  return Math.min(1, a + b);
}

export function createHUDSystem() {
  const hud = new HUD();
  return {
    name: 'ui',
    priority: 95,
    hud,
    init(ctx) { hud.init(ctx); },
    update(dt, ctx) { hud.update(dt, ctx); },
    resize(w, h) { hud.resize(w, h); },
    dispose() { hud.dispose(); },
  };
}
